/**
 * Color Palette Hooks
 *
 * React Query hooks for managing color palettes:
 * - usePalette: Get active palette with cascading resolution
 * - usePalettes: List all available palettes
 * - useCreatePalette: Create custom palette
 * - useUpdatePalette: Update existing palette
 * - useDeletePalette: Delete custom palette
 * - useSetUserPalette: Set user palette preference
 * - useSetOrganizationPalette: Set organization palette
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { colorPalettesApi } from '@/services/api/colorPalettes';
import type {
  ColorPalette,
  GetPalettesParams,
  GetActivePaletteParams,
  CreatePaletteRequest,
  UpdatePaletteRequest,
  SetUserPaletteRequest,
  SetOrganizationPaletteRequest,
} from '@/types';
import { queryOptions } from '@/lib/react-query';

// ============================================================================
// Query Keys
// ============================================================================

export const paletteQueryKeys = {
  all: () => ['palettes'] as const,
  lists: () => ['palettes', 'list'] as const,
  list: (params?: GetPalettesParams) =>
    params ? ['palettes', 'list', params] as const : ['palettes', 'list'] as const,
  systemPalettes: () => ['palettes', 'system'] as const,
  active: (params?: GetActivePaletteParams) =>
    params ? ['palettes', 'active', params] as const : ['palettes', 'active'] as const,
  detail: (id: string) => ['palettes', 'detail', id] as const,
} as const;

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Get the active color palette for the current context
 * Uses cascading resolution: User → Project → Organization → Default
 *
 * @param params - Optional parameters (projectId for project-specific palette)
 * @param enabled - Whether to enable the query (default: true)
 */
export function usePalette(
  params?: GetActivePaletteParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: paletteQueryKeys.active(params),
    queryFn: async () => {
      const response = await colorPalettesApi.getActivePalette(params);
      return response.data;
    },
    ...queryOptions.stable, // Palettes don't change often
    enabled: options?.enabled ?? true,
  });
}

/**
 * Get all accessible color palettes
 * Includes system palettes + custom palettes for the user
 *
 * @param params - Optional filter parameters
 */
export function usePalettes(params?: GetPalettesParams) {
  return useQuery({
    queryKey: paletteQueryKeys.list(params),
    queryFn: async () => {
      const response = await colorPalettesApi.getAllPalettes(params);
      return response.data;
    },
    ...queryOptions.stable,
  });
}

/**
 * Get all system-provided color palettes
 */
export function useSystemPalettes() {
  return useQuery({
    queryKey: paletteQueryKeys.systemPalettes(),
    queryFn: async () => {
      const response = await colorPalettesApi.getSystemPalettes();
      return response.data;
    },
    ...queryOptions.static, // System palettes rarely change
  });
}

/**
 * Get a specific color palette by ID
 *
 * @param paletteId - The palette ID to fetch
 * @param enabled - Whether to enable the query
 */
export function usePaletteById(paletteId: string, enabled = true) {
  return useQuery({
    queryKey: paletteQueryKeys.detail(paletteId),
    queryFn: async () => {
      const response = await colorPalettesApi.getPaletteById(paletteId);
      return response.data.palette;
    },
    ...queryOptions.stable,
    enabled: enabled && !!paletteId,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create a new custom color palette
 */
export function useCreatePalette() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (palette: CreatePaletteRequest) => {
      const response = await colorPalettesApi.createPalette(palette);
      return response.data.palette;
    },
    onSuccess: () => {
      // Invalidate palette lists to refetch
      queryClient.invalidateQueries({ queryKey: paletteQueryKeys.lists() });
    },
  });
}

/**
 * Update an existing color palette
 */
export function useUpdatePalette() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      paletteId,
      updates,
    }: {
      paletteId: string;
      updates: UpdatePaletteRequest;
    }) => {
      const response = await colorPalettesApi.updatePalette(paletteId, updates);
      return response.data.palette;
    },
    onSuccess: (updatedPalette) => {
      // Invalidate specific palette and lists
      queryClient.invalidateQueries({
        queryKey: paletteQueryKeys.detail(updatedPalette._id),
      });
      queryClient.invalidateQueries({ queryKey: paletteQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: paletteQueryKeys.all() });
    },
  });
}

/**
 * Delete a custom color palette
 */
export function useDeletePalette() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paletteId: string) => {
      await colorPalettesApi.deletePalette(paletteId);
      return paletteId;
    },
    onSuccess: (deletedPaletteId) => {
      // Remove from cache and invalidate lists
      queryClient.removeQueries({
        queryKey: paletteQueryKeys.detail(deletedPaletteId),
      });
      queryClient.invalidateQueries({ queryKey: paletteQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: paletteQueryKeys.all() });
    },
  });
}

/**
 * Set the color palette preference for the current user
 */
export function useSetUserPalette() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: SetUserPaletteRequest) => {
      console.log('[usePalette] Mutation starting', { request });
      const response = await colorPalettesApi.setUserPalette(request);
      console.log('[usePalette] Mutation response', { response });
      return response.data;
    },
    onSuccess: async (data) => {
      console.log('[usePalette] Mutation success, refetching queries', {
        data,
        queryKey: paletteQueryKeys.all()
      });

      // Get current query state before refetch
      const activeQueries = queryClient.getQueriesData({
        queryKey: paletteQueryKeys.all()
      });
      console.log('[usePalette] Current query data before refetch', {
        queries: activeQueries.map(([key, data]) => ({ key, data }))
      });

      // Immediately refetch all palette queries
      await queryClient.refetchQueries({ queryKey: paletteQueryKeys.all() });

      // Get query state after refetch
      const refetchedQueries = queryClient.getQueriesData({
        queryKey: paletteQueryKeys.all()
      });
      console.log('[usePalette] Query data after refetch', {
        queries: refetchedQueries.map(([key, data]) => ({ key, data }))
      });
    },
  });
}

/**
 * Clear the color palette preference for the current user
 */
export function useClearUserPalette() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await colorPalettesApi.clearUserPalette();
    },
    onSuccess: () => {
      // Invalidate all palette queries to refetch
      queryClient.invalidateQueries({ queryKey: paletteQueryKeys.all() });
      // Explicitly invalidate active palette queries
      queryClient.invalidateQueries({
        queryKey: ['palettes', 'active'],
        refetchType: 'active'
      });
    },
  });
}

/**
 * Set the color palette for an organization
 * Requires organization admin permissions
 */
export function useSetOrganizationPalette() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: SetOrganizationPaletteRequest) => {
      const response = await colorPalettesApi.setOrganizationPalette(request);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate active palette queries for all users in the organization
      queryClient.invalidateQueries({ queryKey: paletteQueryKeys.all() });
    },
  });
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Get the active palette as CSS custom properties
 * Useful for dynamically injecting palette colors into CSS variables
 */
export function usePaletteAsCSS(params?: GetActivePaletteParams) {
  const { data, isLoading, error } = usePalette(params);

  if (isLoading || error || !data?.palette) {
    return null;
  }

  const palette = data.palette;
  const cssVars: Record<string, string> = {};

  // Primary colors
  if (palette.colors.primary) cssVars['--color-primary'] = palette.colors.primary;
  if (palette.colors.secondary) cssVars['--color-secondary'] = palette.colors.secondary;
  if (palette.colors.accent) cssVars['--color-accent'] = palette.colors.accent;

  // Feedback colors
  if (palette.colors.success) cssVars['--color-success'] = palette.colors.success;
  if (palette.colors.warning) cssVars['--color-warning'] = palette.colors.warning;
  if (palette.colors.error) cssVars['--color-error'] = palette.colors.error;

  // Surface colors
  if (palette.colors.surface?.primary) {
    cssVars['--color-surface-primary'] = palette.colors.surface.primary;
  }
  if (palette.colors.surface?.secondary) {
    cssVars['--color-surface-secondary'] = palette.colors.surface.secondary;
  }
  if (palette.colors.surface?.tertiary) {
    cssVars['--color-surface-tertiary'] = palette.colors.surface.tertiary;
  }

  // Text colors
  if (palette.colors.text?.primary) {
    cssVars['--color-text-primary'] = palette.colors.text.primary;
  }
  if (palette.colors.text?.secondary) {
    cssVars['--color-text-secondary'] = palette.colors.text.secondary;
  }
  if (palette.colors.text?.tertiary) {
    cssVars['--color-text-tertiary'] = palette.colors.text.tertiary;
  }

  // Border colors
  if (palette.colors.border?.primary) {
    cssVars['--color-border-primary'] = palette.colors.border.primary;
  }
  if (palette.colors.border?.secondary) {
    cssVars['--color-border-secondary'] = palette.colors.border.secondary;
  }

  // Time Series Chart colors (8 variables)
  if (palette.colors.timeSeries?.line1) {
    cssVars['--color-timeseries-1'] = palette.colors.timeSeries.line1;
  }
  if (palette.colors.timeSeries?.line2) {
    cssVars['--color-timeseries-2'] = palette.colors.timeSeries.line2;
  }
  if (palette.colors.timeSeries?.line3) {
    cssVars['--color-timeseries-3'] = palette.colors.timeSeries.line3;
  }
  if (palette.colors.timeSeries?.line4) {
    cssVars['--color-timeseries-4'] = palette.colors.timeSeries.line4;
  }
  if (palette.colors.timeSeries?.line5) {
    cssVars['--color-timeseries-5'] = palette.colors.timeSeries.line5;
  }
  if (palette.colors.timeSeries?.line6) {
    cssVars['--color-timeseries-6'] = palette.colors.timeSeries.line6;
  }
  if (palette.colors.timeSeries?.line7) {
    cssVars['--color-timeseries-7'] = palette.colors.timeSeries.line7;
  }
  if (palette.colors.timeSeries?.line8) {
    cssVars['--color-timeseries-8'] = palette.colors.timeSeries.line8;
  }

  // Card Component colors (3 variables - simplified)
  if (palette.colors.cards?.background) {
    cssVars['--color-card-bg'] = palette.colors.cards.background;
  }
  if (palette.colors.cards?.shadow) {
    cssVars['--color-card-shadow'] = palette.colors.cards.shadow;
  }
  if (palette.colors.cards?.border) {
    cssVars['--color-card-border'] = palette.colors.cards.border;
  }

  // Generic chart series colors (0-indexed fallbacks for --color-timeseries-X)
  // These are used as fallbacks in AI-generated charts
  if (palette.colors.timeSeries?.line1) {
    cssVars['--color-chart-series-0'] = palette.colors.timeSeries.line1;
  }
  if (palette.colors.timeSeries?.line2) {
    cssVars['--color-chart-series-1'] = palette.colors.timeSeries.line2;
  }
  if (palette.colors.timeSeries?.line3) {
    cssVars['--color-chart-series-2'] = palette.colors.timeSeries.line3;
  }
  if (palette.colors.timeSeries?.line4) {
    cssVars['--color-chart-series-3'] = palette.colors.timeSeries.line4;
  }
  if (palette.colors.timeSeries?.line5) {
    cssVars['--color-chart-series-4'] = palette.colors.timeSeries.line5;
  }
  if (palette.colors.timeSeries?.line6) {
    cssVars['--color-chart-series-5'] = palette.colors.timeSeries.line6;
  }
  if (palette.colors.timeSeries?.line7) {
    cssVars['--color-chart-series-6'] = palette.colors.timeSeries.line7;
  }
  if (palette.colors.timeSeries?.line8) {
    cssVars['--color-chart-series-7'] = palette.colors.timeSeries.line8;
  }

  return cssVars;
}
